import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, DollarSign } from "lucide-react";
import Modal from "../components/Modal";
import SummaryCard from "../components/SummaryCard";
import { getPaychecks, createPaycheck, updatePaycheck, deletePaycheck } from "../utils/api";
import { toMonthly, calcMonthlyIncome, fmt } from "../utils/calculations";

const FREQ_OPTS = [
  { value: "weekly",       label: "Weekly" },
  { value: "bi-weekly",    label: "Bi-weekly (every 2 weeks)" },
  { value: "semi-monthly", label: "Semi-monthly (twice a month)" },
  { value: "monthly",      label: "Monthly" },
];

const FREQ_LABELS = {
  weekly: "Weekly", "bi-weekly": "Bi-weekly", "semi-monthly": "Semi-monthly", monthly: "Monthly",
};

const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

function ordinal(n) {
  const num = parseInt(n) || 1;
  const s = ["th", "st", "nd", "rd"];
  const v = num % 100;
  return num + (s[(v - 20) % 10] || s[v] || s[0]);
}

const EMPTY = {
  label: "", spouseName: "", amount: "", frequency: "bi-weekly",
  startDate: "", semiMonthlyDay1: "1", semiMonthlyDay2: "15",
};

export default function Paychecks() {
  const [paychecks,     setPaychecks]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [modalOpen,     setModalOpen]     = useState(false);
  const [editing,       setEditing]       = useState(null);
  const [form,          setForm]          = useState(EMPTY);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  async function load() {
    setLoading(true);
    try { setPaychecks(await getPaychecks()); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openAdd() { setEditing(null); setForm(EMPTY); setError(""); setModalOpen(true); }

  function openEdit(p) {
    setEditing(p);
    setForm({
      label:           p.label || "",
      spouseName:      p.spouseName || "",
      amount:          p.amount?.toString() || "",
      frequency:       p.frequency || "bi-weekly",
      startDate:       p.startDate || "",
      semiMonthlyDay1: p.semiMonthlyDay1?.toString() || "1",
      semiMonthlyDay2: p.semiMonthlyDay2?.toString() || "15",
    });
    setError(""); setModalOpen(true);
  }

  function handleChange(e) { setForm((f) => ({ ...f, [e.target.name]: e.target.value })); }

  async function handleSave(e) {
    e.preventDefault(); setError("");
    if (!form.label || !form.amount) { setError("Label and amount are required."); return; }
    if (parseFloat(form.amount) <= 0) { setError("Amount must be greater than 0."); return; }
    if (form.frequency === "semi-monthly" &&
        parseInt(form.semiMonthlyDay1) === parseInt(form.semiMonthlyDay2)) {
      setError("The two pay days must be different."); return;
    }
    setSaving(true);
    try {
      if (editing) {
        const updated = await updatePaycheck(editing.id, form);
        setPaychecks((prev) => prev.map((p) => (p.id === editing.id ? { ...p, ...updated } : p)));
      } else {
        const created = await createPaycheck(form);
        setPaychecks((prev) => [created, ...prev]);
      }
      setModalOpen(false);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    try { await deletePaycheck(id); setPaychecks((prev) => prev.filter((p) => p.id !== id)); }
    catch (err) { alert(err.message); }
    finally { setDeleteConfirm(null); }
  }

  const monthlyIncome = calcMonthlyIncome(paychecks);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Paychecks</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Track each earner's income</p>
        </div>
        <button onClick={openAdd} className="btn-primary"><Plus className="w-4 h-4" /> Add Paycheck</button>
      </div>

      {/* Hero combined-income card — prominent on mobile, becomes first-of-three on sm+. */}
      <div className="sm:hidden card bg-gradient-to-br from-primary-500 to-primary-700 dark:from-primary-700 dark:to-primary-900 border-primary-600 dark:border-primary-800">
        <p className="text-xs font-medium text-primary-100 uppercase tracking-wide">Combined Monthly Income</p>
        <p className="mt-1 text-3xl font-bold text-white">{fmt(monthlyIncome)}</p>
        <p className="mt-1 text-sm text-primary-100">
          from {paychecks.length} paycheck{paychecks.length !== 1 ? "s" : ""} · {fmt(monthlyIncome * 12)}/yr
        </p>
      </div>

      <div className="hidden sm:grid grid-cols-3 gap-4">
        <SummaryCard title="Combined Monthly Income" value={fmt(monthlyIncome)} subtitle={`from ${paychecks.length} paycheck${paychecks.length !== 1 ? "s" : ""}`} icon={DollarSign} color="blue" />
        <SummaryCard title="Annual Household Income" value={fmt(monthlyIncome * 12)} subtitle="estimated" icon={DollarSign} color="green" />
        <SummaryCard title="Earners" value={[...new Set(paychecks.map((p) => p.spouseName).filter(Boolean))].length || paychecks.length} subtitle="income sources" icon={DollarSign} color="purple" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : paychecks.length === 0 ? (
        <div className="card text-center py-16">
          <DollarSign className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">No paychecks yet</h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Add your first paycheck to get started.</p>
          <button onClick={openAdd} className="btn-primary inline-flex"><Plus className="w-4 h-4" /> Add Paycheck</button>
        </div>
      ) : (
        <>
        {/* Mobile: stacked cards. Hidden at md+ where the table below takes over. */}
        <div className="md:hidden space-y-3">
          {paychecks.map((p) => (
            <div key={p.id} className="card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{p.label}</h3>
                  {p.spouseName && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{p.spouseName}</p>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(p)} aria-label="Edit paycheck" className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors">
                    <Pencil className="w-5 h-5" />
                  </button>
                  <button onClick={() => setDeleteConfirm(p.id)} aria-label="Delete paycheck" className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Amount</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{fmt(p.amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Monthly</p>
                  <p className="font-semibold text-secondary-700 dark:text-secondary-400">{fmt(toMonthly(p.amount, p.frequency))}</p>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
                  {FREQ_LABELS[p.frequency] || p.frequency}
                </span>
                {p.frequency === "semi-monthly" && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {ordinal(p.semiMonthlyDay1 || 1)} & {ordinal(p.semiMonthlyDay2 || 15)}
                  </span>
                )}
              </div>
            </div>
          ))}

          {/* Combined total footer card */}
          <div className="card bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Combined Monthly</span>
            <span className="text-sm font-bold text-primary-700 dark:text-primary-400">{fmt(monthlyIncome)}/mo</span>
          </div>
        </div>

        {/* Desktop: original table. */}
        <div className="hidden md:block card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                <th className="px-5 py-3">Label</th>
                <th className="px-5 py-3">Earner</th>
                <th className="px-5 py-3">Amount</th>
                <th className="px-5 py-3">Frequency</th>
                <th className="px-5 py-3 text-right">Monthly</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {paychecks.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-5 py-3.5 font-medium text-gray-900 dark:text-gray-100">{p.label}</td>
                  <td className="px-5 py-3.5 text-gray-600 dark:text-gray-400">{p.spouseName || "—"}</td>
                  <td className="px-5 py-3.5 text-gray-800 dark:text-gray-200">{fmt(p.amount)}</td>
                  <td className="px-5 py-3.5">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
                      {FREQ_LABELS[p.frequency] || p.frequency}
                    </span>
                    {p.frequency === "semi-monthly" && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 pl-0.5">
                        {ordinal(p.semiMonthlyDay1 || 1)} &amp; {ordinal(p.semiMonthlyDay2 || 15)}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right font-semibold text-secondary-700 dark:text-secondary-400">
                    {fmt(toMonthly(p.amount, p.frequency))}/mo
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(p)} className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => setDeleteConfirm(p.id)} className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
                <td colSpan={4} className="px-5 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Combined Monthly Total</td>
                <td className="px-5 py-3 text-right text-sm font-bold text-primary-700 dark:text-primary-400">{fmt(monthlyIncome)}/mo</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
        </>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Paycheck" : "Add Paycheck"}>
        <form onSubmit={handleSave} className="space-y-4">
          {error && <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Paycheck label *</label>
              <input className="input" name="label" maxLength={100} placeholder="e.g. Alex's Job" value={form.label} onChange={handleChange} />
            </div>
            <div>
              <label className="label">Earner name</label>
              <input className="input" name="spouseName" maxLength={100} placeholder="e.g. Alex" value={form.spouseName} onChange={handleChange} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Paycheck amount *</label>
              <input className="input" type="number" inputMode="decimal" name="amount" min="0" step="0.01" placeholder="0.00" value={form.amount} onChange={handleChange} />
            </div>
            <div>
              <label className="label">Pay frequency *</label>
              <select className="input" name="frequency" value={form.frequency} onChange={handleChange}>
                {FREQ_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {form.frequency === "semi-monthly" && (
            <div>
              <label className="label">Pay days of the month</label>
              <div className="flex items-center gap-2 flex-wrap">
                <select className="input flex-1 min-w-0" name="semiMonthlyDay1" value={form.semiMonthlyDay1} onChange={handleChange}>
                  {DAYS.map((d) => <option key={d} value={d}>{ordinal(d)}</option>)}
                </select>
                <span className="text-sm text-gray-500 dark:text-gray-400">and</span>
                <select className="input flex-1 min-w-0" name="semiMonthlyDay2" value={form.semiMonthlyDay2} onChange={handleChange}>
                  {DAYS.map((d) => <option key={d} value={d}>{ordinal(d)}</option>)}
                </select>
              </div>
            </div>
          )}
          <div>
            <label className="label">First pay date (for calendar)</label>
            <input className="input" type="date" name="startDate" value={form.startDate} onChange={handleChange} />
          </div>
          {form.amount && form.frequency && (
            <div className="p-3 bg-primary-50 dark:bg-primary-900/30 rounded-lg text-sm text-primary-800 dark:text-primary-300">
              Monthly equivalent: <strong>{fmt(toMonthly(parseFloat(form.amount) || 0, form.frequency))}</strong>
            </div>
          )}
          <div className="sticky-actions flex gap-3 pt-3 pb-1">
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Paycheck"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete Paycheck" size="sm">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">Are you sure you want to delete this paycheck? This action cannot be undone.</p>
        <div className="flex gap-3">
          <button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button onClick={() => handleDelete(deleteConfirm)} className="btn-danger flex-1 justify-center">Delete</button>
        </div>
      </Modal>
    </div>
  );
}
